import { message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { get, post } from '../../axios/axios';
import { PAY_TYPE, isAlipayRedirect, payClient } from './index';

const EMPTY = { alipay: {}, wechat: {} };
const LOADING = { alipay: { loading: true }, wechat: { loading: true } };
// 手机只下支付宝一单
const LOADING_MOBILE = { alipay: { loading: true }, wechat: {} };

/** 到账后在 window 上派发的事件：detail = { payNo, orderNo, kind }（埋点等监听） */
export const PAID_EVENT = 'lgfb:order-paid';

// 支付宝 return_url 最长 256 字符，站内路径留一些余量给域名
const RETURN_PATH_MAX = 200;

const isUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

/** 按付款单号前缀判断业务：writing 写作全文 / chapter 单章 / upgrade 补差价 / ppt 答辩PPT / format 格式精修 */
export const orderKind = (payNo) => {
    const no = typeof payNo === 'string' ? payNo : '';
    if (no.startsWith('MLWC')) return 'chapter';
    if (no.startsWith('MLWU')) return 'upgrade';
    if (no.startsWith('MLW')) return 'writing';
    if (no.startsWith('MLPG')) return 'ppt';
    if (no.startsWith('MLFM')) return 'format';
    return 'other';
};

const WRITING_KINDS = ['writing', 'chapter', 'upgrade'];

/**
 * 手机端支付宝付完 / 取消后回到的站内路径：写作订单回订单结果页（自己查单、展示进度）；
 * 其余回当前页并带上 paid=付款单号（答辩PPT 页按 ?task= 恢复任务，格式精修页按本机记住的订单确认到账）。
 */
export const returnPathFor = (payNo, loc = typeof window === 'undefined' ? {} : window.location) => {
    const no = encodeURIComponent(payNo || '');
    if (WRITING_KINDS.includes(orderKind(payNo))) return `/order/result?no=${no}`;
    const pathname = loc?.pathname || '/';
    const params = new URLSearchParams(loc?.search || '');
    params.set('paid', payNo || '');
    const full = `${pathname}?${params.toString()}`;
    return full.length > RETURN_PATH_MAX ? `${pathname}?paid=${no}` : full;
};

/** 通知页面其它部分（埋点等）某笔付款已到账 */
export const notifyOrderPaid = (payNo, { orderNo, kind } = {}) => {
    if (typeof window === 'undefined' || typeof window.CustomEvent !== 'function') return;
    window.dispatchEvent(
        new CustomEvent(PAID_EVENT, {
            detail: { payNo, orderNo: orderNo || payNo, kind: kind || orderKind(payNo) },
        }),
    );
};

/**
 * 灵感风暴现有支付：同一个订单号分别向支付宝、微信下单。
 * 手机（client=H5）的支付宝单是手机网站支付，returnUrl 是付完回到的站内路径。
 */
export const createPayment = async (orderNo, payType, { returnUrl } = {}) => {
    const body = { orderNo, client: payClient(), payType };
    if (returnUrl) body.returnUrl = returnUrl;
    const res = await post('/member/payment/pay', body, { slient: true });
    return res?.data;
};

export const queryPaid = async (orderNo) => {
    const res = await get(`/member/payment/pay/result/${orderNo}`, {}, { slient: true });
    return res?.data?.code === 0;
};

export const redeemCode = async (orderNo, code) => {
    const res = await post('/member/payment/redeemCodeNew', { orderNo, code: String(code).trim() });
    if (res?.code !== 0 || res?.data !== true) throw new Error(res?.msg || '兑换失败，请检查兑换码');
    return true;
};

/**
 * 付款弹窗里输入兑换码 / 套餐码 / 优惠券：
 * 返回 { result: 'PAID' | 'DISCOUNTED' | 'NEED_REVOKE', code, couponAmount, orderAmount, discountAmount, payableAmount, previousOrderNo }。
 * PAID 已整单兑换到账；DISCOUNTED 优惠券面额不够，已抵扣、还需支付 payableAmount；NEED_REVOKE 优惠券挂在别的未支付订单上，要先撤回。
 */
export const applyRedeem = async (orderNo, code) => {
    const res = await post('/member/payment/coupon/apply', { orderNo, code: String(code).trim() });
    if (res?.code !== 0 || !res?.data?.result) throw new Error(res?.msg || '兑换失败，请检查兑换码');
    return res.data;
};

/** 撤回挂在未支付订单上的优惠券 */
export const revokeRedeem = async (code) => {
    const res = await post('/member/payment/coupon/revoke', { code: String(code).trim() });
    if (res?.code !== 0 || res?.data !== true) throw new Error(res?.msg || '撤回失败，请稍后重试');
    return true;
};

/** 订单当前挂着的优惠券抵扣；没有返回 null */
export const appliedRedeem = async (orderNo) => {
    const res = await get('/member/payment/coupon/applied', { orderNo }, { slient: true });
    return res?.code === 0 && res?.data?.result ? res.data : null;
};

/** 下单接口返回的 data → 通道状态 */
const toChannel = (payType, data, mobile) => {
    if (payType === PAY_TYPE.ALIPAY) {
        if (!data?.form) return { error: true };
        // 手机：付款链接放在 payUrl（不放 form），PayQRCodes 显示支付宝按钮
        if (mobile) return isUrl(data.form) ? { payUrl: data.form } : { form: data.form };
        return { form: data.form };
    }
    return data?.codeUrl || data?.mwebUrl ? { codeUrl: data.codeUrl, mwebUrl: data.mwebUrl } : { error: true };
};

/**
 * 一个订单号的付款码 + 轮询到账。start(no, { orderNo, kind, returnUrl }) 开始，到账回调 onPaid(no)，
 * 同时在 window 上派发 lgfb:order-paid 事件。
 * 电脑：支付宝、微信双码；手机：只下支付宝手机网站支付单（channels.alipay.payUrl），原页面继续查单。
 */
export default function usePayOrder({ onPaid, interval = 1500, maxAttempts = 400 } = {}) {
    const [channels, setChannels] = useState(EMPTY);
    const [orderNo, setOrderNo] = useState('');
    const state = useRef({ requestId: 0, timer: null, pending: null, meta: {}, returnUrl: '' });
    const onPaidRef = useRef(onPaid);
    onPaidRef.current = onPaid;

    const stop = useCallback(() => {
        state.current.requestId += 1;
        if (state.current.timer) clearTimeout(state.current.timer);
        state.current.timer = null;
        state.current.pending = null;
    }, []);

    const poll = useCallback(
        async (requestId, no, attempt = 1) => {
            if (requestId !== state.current.requestId) return;
            if (attempt > maxAttempts) {
                message.warning('查询支付结果超时，如已付款请到「订单与下载」查询');
                return;
            }
            try {
                if (await queryPaid(no)) {
                    if (requestId !== state.current.requestId) return;
                    const meta = state.current.meta;
                    stop();
                    notifyOrderPaid(no, meta);
                    onPaidRef.current?.(no);
                    return;
                }
            } catch {
                // 网络抖动继续轮询
            }
            if (requestId !== state.current.requestId) return;
            state.current.pending = { requestId, no, attempt: attempt + 1 };
            state.current.timer = setTimeout(() => poll(requestId, no, attempt + 1), interval);
        },
        [interval, maxAttempts, stop],
    );

    // 手机付完从支付宝切回来时后台定时器可能被挂起，页面一可见就立刻查一次
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const onVisible = () => {
            if (document.visibilityState !== 'visible') return;
            const { pending, timer, requestId } = state.current;
            if (!pending || !timer || pending.requestId !== requestId) return;
            clearTimeout(timer);
            state.current.timer = null;
            poll(pending.requestId, pending.no, pending.attempt);
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [poll]);

    const loadChannel = useCallback(async (no, payType, requestId) => {
        const key = payType === PAY_TYPE.ALIPAY ? 'alipay' : 'wechat';
        const mobile = isAlipayRedirect();
        setChannels((prev) => ({ ...prev, [key]: { loading: true } }));
        try {
            const options = mobile && payType === PAY_TYPE.ALIPAY ? { returnUrl: state.current.returnUrl } : {};
            const data = await createPayment(no, payType, options);
            if (requestId !== state.current.requestId) return;
            setChannels((prev) => ({ ...prev, [key]: toChannel(payType, data, mobile) }));
        } catch (e) {
            if (requestId !== state.current.requestId) return;
            setChannels((prev) => ({ ...prev, [key]: { error: true } }));
        }
    }, []);

    const start = useCallback(
        (no, { orderNo: bizOrderNo, kind, returnUrl } = {}) => {
            stop();
            const requestId = state.current.requestId;
            state.current.meta = { orderNo: bizOrderNo, kind };
            setOrderNo(no);
            if (isAlipayRedirect()) {
                state.current.returnUrl = returnUrl || returnPathFor(no);
                setChannels(LOADING_MOBILE);
                loadChannel(no, PAY_TYPE.ALIPAY, requestId);
            } else {
                state.current.returnUrl = '';
                setChannels(LOADING);
                loadChannel(no, PAY_TYPE.ALIPAY, requestId);
                loadChannel(no, PAY_TYPE.WECHAT, requestId);
            }
            poll(requestId, no);
        },
        [loadChannel, poll, stop],
    );

    const retry = useCallback(
        (payType) => {
            if (!orderNo) return;
            if (isAlipayRedirect() && payType !== PAY_TYPE.ALIPAY) return;
            loadChannel(orderNo, payType, state.current.requestId);
        },
        [loadChannel, orderNo],
    );

    const reset = useCallback(() => {
        stop();
        setChannels(EMPTY);
        setOrderNo('');
    }, [stop]);

    useEffect(() => stop, [stop]);

    return { channels, orderNo, start, retry, reset, stop };
}
